import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar,
  Divider,
  Fade,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "../../../../firebase";

export default function AdminReviewDialog({
  adminNotes,
  adminName = "Admin",
  adminAvatarPath,
  timestamp,
  sentiment, // "approved" | "rejected" | "pending"
}) {
  const [open, setOpen] = useState(false);
  const [adminAvatarURL, setAvatarURL] = useState("");
  console.log("admin path", adminAvatarPath);
  useEffect(() => {
    console.log(adminAvatarPath);
    // Only try to fetch if we have a path
    if (adminAvatarPath) {
      const avatarRef = ref(storage, adminAvatarPath);
      console.log("[DEBUG]: AvatarRef = ", avatarRef);
      getDownloadURL(avatarRef)
        .then((url) => setAvatarURL(url))
        .catch((err) => setAvatarURL("")); // fallback if no avatar
    } else {
      setAvatarURL("");
    }
  }, [adminAvatarPath]);

  const handleToggle = () => setOpen((prev) => !prev);

  // Accent color by sentiment
  const accent = {
    approved: "#B2F5C5",
    rejected: "#FFD6D6",
    pending: "#FFF7CC",
    neutral: "#D6E2FF",
  }[sentiment || "neutral"];

  return (
    <Box
      sx={{
        position: "fixed",
        left: "56%",
        bottom: 80,
        transform: "translateX(-50%)",
        zIndex: (theme) => theme.zIndex.appBar + 1,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        userSelect: "none",
        background: `linear-gradient(90deg, ${accent} 0 6px, rgba(9,13,15,0.97) 8px 100%)`,
        padding: "6px 16px",
        borderRadius: 2,
        boxShadow: "0 2px 18px rgba(0,0,0,0.30)",
        minWidth: 120,
        "&:hover": {
          background: `linear-gradient(90deg, ${accent} 0 6px, #24292f 8px 100%)`,
          boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
        },
      }}
      aria-label="Toggle Admin Review Dialog"
      role="button"
      tabIndex={0}
      onClick={handleToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleToggle();
        }
      }}
    >
      <AdminPanelSettingsIcon sx={{ mr: 1, color: accent, fontSize: 24 }} />
      <Typography
        variant="body1"
        sx={{
          color: "#fff",
          fontWeight: 700,
          mr: 1,
          userSelect: "none",
          letterSpacing: 0.2,
        }}
      >
        Admin Feedback
      </Typography>
      <Box
        component="span"
        sx={{
          transition: "transform 0.2s",
          display: "flex",
          alignItems: "center",
        }}
      >
        {open ? (
          <KeyboardArrowDownIcon sx={{ color: "#fff" }} />
        ) : (
          <KeyboardArrowUpIcon sx={{ color: "#fff" }} />
        )}
      </Box>

      <Dialog
        open={open}
        onClose={handleToggle}
        aria-labelledby="admin-review-dialog-title"
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            marginLeft: 38,
            marginBottom: 30,
            borderRadius: 3,
            backgroundColor: "#181b20", // Dialog bg
            boxShadow: "0 6px 32px rgba(25,40,80,0.24)",
          },
        }}
      >
        <DialogTitle
          id="admin-review-dialog-title"
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            bgcolor: "rgba(9, 13, 15, 0.95)", // status accent
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            minHeight: 56,
          }}
        >
          {adminAvatarPath ? (
            <Avatar src={adminAvatarURL} alt={adminName} />
          ) : (
            <Avatar>
              <AdminPanelSettingsIcon />
            </Avatar>
          )}
          <Box>
            <Typography fontWeight={700} fontSize={16} color="#fff">
              {adminName}
            </Typography>
            {timestamp && sentiment !== "pending" && (
              <Typography fontSize={13} color="#EEEEF1">
                {sentiment === "approved"
                  ? "Approved At: "
                  : sentiment === "rejected"
                  ? "Rejected At: "
                  : ""}
                {timestamp.toDate().toLocaleString()}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1 }} />
          <IconButton
            aria-label="close"
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            sx={{
              color: "#444",
              ml: 2,
            }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2, bgcolor: "#DBDBDD" }}>
          <Typography
            sx={{
              background: "#e7f1ff", // matches folder icon fill
              p: 1.5,
              borderRadius: 1.5,
              color: "#222b45", // deep blue (high contrast, matches FolderCard)
              fontWeight: 500,
              fontSize: 15,
              letterSpacing: 0.03,
            }}
          >
            {adminNotes || (
              <span style={{ color: "#888" }}>No feedback provided.</span>
            )}
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
